/**
 * storeRef — a tiny indirection to break an import cycle.
 *
 * The cycle is:
 *   apiClient.ts → store/index.ts → userSlice.ts → apiClient.ts
 *
 * It is NOT benign: `store/index.ts` calls `configureStore({ reducer:
 * { user: userReducer, ... } })` at module-evaluation time, so when the
 * cycle reaches that line `userReducer` may still be uninitialised —
 * producing "Cannot access '{default export}' before initialization".
 *
 * The fix: `apiClient` must not import `store` (or any slice) directly.
 * Instead:
 *   - `store/index.ts` calls `registerStore(store)` once, right after
 *     it builds the store.
 *   - `apiClient` calls `getStore()` lazily, only inside the response
 *     interceptor (i.e. at request time, long after all modules have
 *     finished evaluating).
 *
 * This file imports nothing from the store or the slices, so it sits
 * outside the cycle entirely.
 */

// We type the store + the one action we need structurally, to avoid
// importing the concrete types (which would re-introduce the cycle).
type Dispatchable = { type: string; payload?: unknown };
export interface MinimalStore {
  dispatch: (action: Dispatchable) => unknown;
}

let _store: MinimalStore | null = null;

/** Called once by store/index.ts after the store is created. */
export function registerStore(store: MinimalStore): void {
  _store = store;
}

/** Returns the store, or null if it hasn't been registered yet. */
export function getStore(): MinimalStore | null {
  return _store;
}
